import React, { PropsWithChildren } from 'react'
import { Absolute } from '../bases/Absolute'
import { Center } from '../bases/Center'
import { VStack } from '../bases/VStack'
import { Text } from '../bases/Text'
import { NftState } from '@/src/graphql/generated/types'
import { CardFrame, CardSize } from './common/CardFrame'
import { NFTTitleText } from './common/NFTTitleText'
import { NFTSubTitleText } from './common/NFTSubTitleText'
import { EnergyArcadeMachine } from './EnergyArcadeMachine'
import { MegaSparkingLabel } from '../am/MegaSparkingLabel'
import Image from 'next/image'
import megasparkingAM from '@/public/images/megasparking_03.png'

const labelStyles = {
  S: { paddingLeft: 14, paddingRight: 14, marginTop: 4 },
  M: { paddingLeft: 14, paddingRight: 14, marginTop: 4 },
  L: { paddingLeft: 14, paddingRight: 14, marginTop: 8 },
}

type Props = {
  imageUrl: string
  id: string
  name: string
  state?: NftState
  onClick?: () => void
  cardSize: CardSize
  energy?: number
  maxEnergy?: number
  extractedEnergy?: number
  installable?: boolean
  extractable?: boolean
  dismantable?: boolean
  feverSparkRemain?: number | null
  hiddenMegaSparkingLabel?: boolean
  disabled?: boolean
  loading?: boolean
}

export const ArcadeMachineCard = (props: PropsWithChildren<Props>) => {
  const showBadge =
    props.extractable !== undefined || props.installable !== undefined || props.dismantable !== undefined
  return (
    <CardFrame
      key={props.id}
      cardSize={props.cardSize}
      state={props.state ?? NftState.InAkiverse}
      onClick={props.onClick}
      disabled={props.disabled}
      loading={props.loading}
    >
      <VStack className="h-full w-full bg-[url('/images/gameCenterCellTextBg.png')] bg-repeat">
        <EnergyArcadeMachine
          imageUrl={props.imageUrl}
          maxEnergy={props.maxEnergy}
          energy={props.energy}
          extractedEnergy={props.extractedEnergy}
          feverSparkRemain={props.feverSparkRemain}
          cardSize={props.cardSize}
        >
          {props.feverSparkRemain !== null && props.feverSparkRemain !== 0 && (
            <Absolute className="top-0 -left-2 h-full w-full">
              <Image src={megasparkingAM} alt="megasparkingAM" />
            </Absolute>
          )}
          {!props.hiddenMegaSparkingLabel &&
            props.feverSparkRemain !== null &&
            props.feverSparkRemain !== undefined && (
              <MegaSparkingLabel
                feverSparkRemain={props.feverSparkRemain}
                className={`${showBadge ? 'bottom-12' : 'bottom-2'}`}
                cardSize={props.cardSize}
              />
            )}
          {showBadge && <CreateBadgeList {...props} />}
        </EnergyArcadeMachine>
        <VStack className="flex w-full flex-col" style={labelStyles[props.cardSize]}>
          <NFTTitleText cardSize={props.cardSize} text={props.name} />
          <NFTSubTitleText cardSize={props.cardSize} text={props.id} />
        </VStack>
      </VStack>
    </CardFrame>
  )
}

const CreateBadgeList = (props: Props) => {
  // 優先度順に並べて先頭二つまでを表示
  const showBadges = [
    { display: props.installable, text: 'Install' },
    { display: props.extractable, text: 'Extract' },
    { display: props.dismantable, text: 'Dismantle' },
  ]
    .filter((e) => e.display)
    .slice(0, 2)
  return (
    <Absolute className="bottom-2 right-3 items-center gap-x-2 overflow-hidden">
      {showBadges.map((e) => (
        <CreateBadge key={e.text} text={e.text} cardSize={props.cardSize} />
      ))}
    </Absolute>
  )
}

const CreateBadge = (props: { text: string; cardSize: CardSize }) => {
  const size = {
    S: { width: 60, height: 18, fontSize: 'text-xs' },
    M: { width: 70, height: 20, fontSize: 'text-xs' },
    L: { width: 92, height: 32, fontSize: 'text-base' },
  }[props.cardSize]
  return (
    <div
      className="items-center overflow-hidden  rounded-[100px] border-2 border-[#FFCBF1] bg-akiverse-pink bg-opacity-30"
      style={{ width: size.width, height: size.height, flexShrink: 0 }}
    >
      <Center className="h-full w-full">
        <Text className={`font-bold text-white ${size.fontSize}`}>{props.text}</Text>
      </Center>
    </div>
  )
}
